import { openDB, type IDBPDatabase } from 'idb';
import type { Song, Playlist, StorageAdapter } from '@/types';

const DB_NAME = 'zemer-music-db';
const DB_VERSION = 2; // Bumped version for migration

interface ZemerDB {
  favorites: {
    key: string;
    value: Song & { addedAt?: number };
  };
  playlists: {
    key: string;
    value: Playlist;
  };
  history: {
    key: number;
    value: {
      song: Song;
      timestamp: number;
    };
  };
  settings: {
    key: string;
    value: unknown;
  };
}

class IndexedDBStorage implements StorageAdapter {
  private dbPromise: Promise<IDBPDatabase<ZemerDB>> | null = null;
  private initError: Error | null = null;

  constructor() {
    this.dbPromise = this.initDB();
  }

  private async initDB(): Promise<IDBPDatabase<ZemerDB>> {
    try {
      const db = await openDB<ZemerDB>(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion) {
          console.log(`Upgrading IndexedDB from v${oldVersion} to v${newVersion}`);

          // Favorites store
          if (!db.objectStoreNames.contains('favorites')) {
            const favStore = db.createObjectStore('favorites', { keyPath: 'videoId' });
            favStore.createIndex('by-added', 'addedAt');
          }

          // Playlists store
          if (!db.objectStoreNames.contains('playlists')) {
            db.createObjectStore('playlists', { keyPath: 'id' });
          }

          // History store
          if (!db.objectStoreNames.contains('history')) {
            const historyStore = db.createObjectStore('history', {
              keyPath: 'timestamp',
              autoIncrement: false,
            });
            historyStore.createIndex('by-timestamp', 'timestamp');
          }

          // Settings store
          if (!db.objectStoreNames.contains('settings')) {
            db.createObjectStore('settings');
          }
        },
        blocked() {
          console.warn('IndexedDB blocked - close other tabs using this app');
        },
        blocking() {
          console.warn('IndexedDB blocking - new version waiting');
        },
        terminated() {
          console.error('IndexedDB connection terminated unexpectedly');
        },
      });

      console.log('IndexedDB initialized successfully');
      return db;
    } catch (error) {
      this.initError = error instanceof Error ? error : new Error(String(error));
      console.error('Failed to initialize IndexedDB:', error);
      throw error;
    }
  }

  private async getDB(): Promise<IDBPDatabase<ZemerDB> | null> {
    if (this.initError) {
      console.warn('IndexedDB unavailable due to init error:', this.initError.message);
      return null;
    }
    try {
      return await this.dbPromise;
    } catch (error) {
      console.error('Failed to get IndexedDB connection:', error);
      return null;
    }
  }

  async isAvailable(): Promise<boolean> {
    const db = await this.getDB();
    return db !== null;
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      const db = await this.getDB();
      if (!db) return null;
      const value = await db.get('settings', key);
      return (value as T) ?? null;
    } catch (error) {
      console.error('Failed to get from IndexedDB:', error);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    try {
      const db = await this.getDB();
      if (!db) return;
      await db.put('settings', value, key);
    } catch (error) {
      console.error('Failed to set in IndexedDB:', error);
    }
  }

  async delete(key: string): Promise<void> {
    try {
      const db = await this.getDB();
      if (!db) return;
      await db.delete('settings', key);
    } catch (error) {
      console.error('Failed to delete from IndexedDB:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      const db = await this.getDB();
      if (!db) return;
      await db.clear('settings');
    } catch (error) {
      console.error('Failed to clear IndexedDB settings:', error);
    }
  }

  // Favorites methods
  async addFavorite(song: Song): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      const songWithTimestamp = { ...song, addedAt: Date.now() };
      await db.put('favorites', songWithTimestamp);
      return true;
    } catch (error) {
      console.error('Failed to add favorite to IndexedDB:', error);
      return false;
    }
  }

  async removeFavorite(videoId: string): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      await db.delete('favorites', videoId);
      return true;
    } catch (error) {
      console.error('Failed to remove favorite from IndexedDB:', error);
      return false;
    }
  }

  async getFavorites(): Promise<Song[]> {
    try {
      const db = await this.getDB();
      if (!db) return [];
      const favorites = await db.getAll('favorites');
      // Sort by addedAt descending (newest first)
      return favorites.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
    } catch (error) {
      console.error('Failed to get favorites from IndexedDB:', error);
      return [];
    }
  }

  async isFavorite(videoId: string): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      const song = await db.get('favorites', videoId);
      return !!song;
    } catch (error) {
      console.error('Failed to check favorite in IndexedDB:', error);
      return false;
    }
  }

  // Playlist methods
  async createPlaylist(playlist: Playlist): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      await db.add('playlists', playlist);
      return true;
    } catch (error) {
      console.error('Failed to create playlist in IndexedDB:', error);
      return false;
    }
  }

  async updatePlaylist(playlist: Playlist): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      await db.put('playlists', playlist);
      return true;
    } catch (error) {
      console.error('Failed to update playlist in IndexedDB:', error);
      return false;
    }
  }

  async deletePlaylist(id: string): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      await db.delete('playlists', id);
      return true;
    } catch (error) {
      console.error('Failed to delete playlist from IndexedDB:', error);
      return false;
    }
  }

  async getPlaylist(id: string): Promise<Playlist | null> {
    try {
      const db = await this.getDB();
      if (!db) return null;
      const playlist = await db.get('playlists', id);
      return playlist ?? null;
    } catch (error) {
      console.error('Failed to get playlist from IndexedDB:', error);
      return null;
    }
  }

  async getAllPlaylists(): Promise<Playlist[]> {
    try {
      const db = await this.getDB();
      if (!db) return [];
      return db.getAll('playlists');
    } catch (error) {
      console.error('Failed to get playlists from IndexedDB:', error);
      return [];
    }
  }

  // History methods
  async addToHistory(song: Song): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      const timestamp = Date.now();
      await db.put('history', { song, timestamp });

      // Keep only last 100 items
      const allHistory = await db.getAllFromIndex('history', 'by-timestamp');
      if (allHistory.length > 100) {
        const toDelete = allHistory.slice(0, allHistory.length - 100);
        const tx = db.transaction('history', 'readwrite');
        for (const item of toDelete) {
          await tx.store.delete(item.timestamp);
        }
        await tx.done;
      }
      return true;
    } catch (error) {
      console.error('Failed to add to history in IndexedDB:', error);
      return false;
    }
  }

  async getHistory(limit: number = 50): Promise<Array<{ song: Song; timestamp: number }>> {
    try {
      const db = await this.getDB();
      if (!db) return [];
      const all = await db.getAllFromIndex('history', 'by-timestamp');
      return all.slice(-limit).reverse();
    } catch (error) {
      console.error('Failed to get history from IndexedDB:', error);
      return [];
    }
  }

  async clearHistory(): Promise<boolean> {
    try {
      const db = await this.getDB();
      if (!db) return false;
      await db.clear('history');
      return true;
    } catch (error) {
      console.error('Failed to clear history in IndexedDB:', error);
      return false;
    }
  }

  // Sync methods for loading data into store
  async syncToStore(): Promise<{
    favorites: Song[];
    playlists: Playlist[];
    history: Array<{ song: Song; timestamp: number }>;
  }> {
    const [favorites, playlists, history] = await Promise.all([
      this.getFavorites(),
      this.getAllPlaylists(),
      this.getHistory(),
    ]);

    console.log('IndexedDB sync complete:', {
      favorites: favorites.length,
      playlists: playlists.length,
      history: history.length,
    });

    return { favorites, playlists, history };
  }
}

// Export singleton
export const storage = new IndexedDBStorage();

// Export class for testing
export { IndexedDBStorage };
